// Compact tap-row renderer for SurveyJS `ranging-status-*` matrixdropdown
// questions in Field v2 (card #4031, behind field:ranging_confirm).
//
// It draws over the SAME SurveyJS question: the native table is hidden, and
// every tap writes `{ [sku]: { status } }` straight into question.value, so
// result_json keeps its shape and Response#extract_stock_events is untouched.
// The renderer owns click handling because re-tapping an already-checked
// native radio fires no change event.
//
// Last visit's value (`hints`, a separate payload) shows as a faded ring only.
// It is never written to the question value, so an unanswered row stays
// unanswered until the tasker taps it. Tapping the ghost confirms it; tapping
// another button picks that one and a small "was C" appears beside the row.
//
// Row visibility for the #3070 search bar is read from the same survey
// variables SurveyBaseController sets, so search keeps working.
//
// An open allocation/TIO shows as an "Incoming" badge (`incoming`, also a
// separate payload, card #4692) on both the row and the pinned scan card. It
// never answers a row either -- same travel rules as the last-visit hint.
import { RANGING_VALUES, RANGING_LABELS, applyRangingState } from "./ranging_status_styles"

const RANGING_NAME = /^ranging-status/i
const CONTAINER_CLASS = "ranging-confirm"

export function isRangingStatusQuestion(question) {
  return question.getType() === "matrixdropdown" &&
         RANGING_NAME.test(question.name || "") &&
         question.rows?.length > 0
}

// Card #4692 -- open allocation/TIO incoming-stock badge, shown on both the
// tap row and the pinned scan card. Static per render (the hint doesn't
// change while the question is on screen), so it is built once per row/pin
// rather than repainted on every tap.
//
// despatch_date is an ISO date-only string ("YYYY-MM-DD"); parsed as local
// y/m/d rather than via `new Date(iso)` (which reads it as UTC midnight and
// can display a day early west of UTC).
function formatDespatchDate(iso) {
  const parts = String(iso || "").split("-").map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null
  const [year, month, day] = parts
  return new Date(year, month - 1, day).toLocaleDateString("en-AU", { day: "numeric", month: "short" })
}

function incomingBadgeLabel(incoming) {
  const date = formatDespatchDate(incoming?.despatch_date)
  const quantity = incoming?.quantity
  const parts = [quantity != null ? `${quantity} incoming` : "Incoming"]
  if (date) parts.push(date)
  return parts.join(" · ")
}

function applyIncomingBadge(badge, incoming) {
  if (!incoming) {
    badge.classList.add("hidden")
    badge.classList.remove("inline-flex")
    badge.textContent = ""
    return
  }
  badge.textContent = incomingBadgeLabel(incoming)
  badge.title = "Stock allocated but not yet confirmed on shelf"
  badge.classList.remove("hidden")
  badge.classList.add("inline-flex")
}

// Strips the same stray whitespace/control characters Response#clean_sku does,
// so a row's SKU matches the (cleaned) SKU the hint was stored under.
function normaliseSku(sku) {
  return String(sku ?? "").replace(/[\u0000-\u001f\u007f]/g, "").trim().replace(/ {2,}/g, " ")
}

// Barcode scan (search box button). The camera sheet is the shared
// field-barcode-scanner (views/field/shared/_ranging_barcode_scanner), opened
// and read through document events. Only the question that opened it handles
// the result.
//
// #4120: the scanner is a docked strip that stays open between scans. A matching
// scan pins the product in a card under the camera (cloned from the partial's
// template) whose I/O/C/X buttons answer through the same tap() as the row
// buttons; the next scan replaces it. Without that template the older behaviour
// (scroll to and highlight the row) still applies.
const SCANNER_ID = "ranging-barcode-scanner"
const PINNED_TEMPLATE_ID = "ranging-pinned-template"
const PINNED_SLOT_SELECTOR = "[data-ranging-pinned-slot]"
const NO_MATCH_ID = "ranging-barcode-no-match"
const NO_MATCH_VISIBLE_MS = 2500
let activeScanHandler = null
let activeScanQuestionName = null

document.addEventListener("field-barcode-scanner:scanned", event => {
  if (event.target?.id !== SCANNER_ID) return
  activeScanHandler?.(String(event.detail?.barcode ?? ""))
})

function scannerAvailable() {
  return !!document.getElementById(SCANNER_ID)
}

const stripZeros = code => String(code).trim().replace(/^0+/, "")

function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}

function allowedValues(question) {
  const column = question.columns?.find(c => c.name === "status") || question.columns?.[0]
  const choices = (column?.choices?.length ? column.choices : question.choices) || []
  const values = choices.map(choice => String(choice.value)).filter(v => RANGING_VALUES.includes(v))
  return values.length ? values : RANGING_VALUES
}

export function renderRangingConfirm({ survey, question, htmlElement, hints, incoming = {}, barcodes = {}, searchVarName }) {
  htmlElement.querySelector(`.${CONTAINER_CLASS}`)?.remove()
  // A re-render must retire the previous render's observers, or they fight over the search box.
  question.__rangingCleanups?.forEach(cleanup => cleanup())
  question.__rangingCleanups = []
  if (question.__rangingConfirmHandler) {
    survey.onValueChanged.remove(question.__rangingConfirmHandler)
    question.__rangingConfirmHandler = null
  }

  const hintBySku = new Map(Object.entries(hints || {}).map(([sku, value]) => [normaliseSku(sku), value]))
  const incomingBySku = new Map(Object.entries(incoming || {}).map(([sku, value]) => [normaliseSku(sku), value]))
  const values = allowedValues(question)
  const rows = []
  let lastTappedIndex = -1

  const container = el("div", `${CONTAINER_CLASS} w-full`)

  // Progress. An in-flow counter sits at the top of the question; a compact
  // floating pill (counter + Next) is fixed top-right under the page header
  // while any of the question is on screen. position: sticky can't be used --
  // SurveyJS wraps the question in its own scroll containers.
  const header = el("div", "flex items-center py-2 mb-1 border-b border-slate-100")
  const counter = el("p", "text-sm font-semibold text-slate-700")
  header.append(counter)

  const pill = el("div", "fixed top-16 right-3 z-[9] hidden items-center gap-1 pl-3 pr-0.5 h-11 rounded-full bg-white shadow-md border border-slate-200")
  const pillCounter = el("span", "text-xs font-bold tabular-nums text-slate-700")
  pillCounter.setAttribute("aria-live", "polite")
  const nextButton = el("button", "size-11 -my-px flex items-center justify-center rounded-full bg-sapphire text-white text-base font-bold active:opacity-80", "↓")
  nextButton.type = "button"
  nextButton.setAttribute("aria-label", "Next unconfirmed")
  nextButton.title = "Next unconfirmed"
  nextButton.addEventListener("click", () => jumpToNextUnconfirmed())
  pill.append(pillCounter, nextButton)

  const list = el("div", "flex flex-col gap-1")
  const noMatches = el("p", "hidden py-6 text-center text-sm text-slate-400", "No matching rows")
  container.append(header, pill, list, noMatches)

  const currentStatus = sku => (question.value || {})[sku]?.status || null

  question.rows.forEach(rowItem => {
    const sku = String(rowItem.value)
    const name = String(rowItem.text || "")
    const hint = hintBySku.get(normaliseSku(sku)) || null
    const incomingHint = incomingBySku.get(normaliseSku(sku)) || null

    const row = el("div", "ranging-confirm-row flex flex-col gap-2 px-3 py-3 rounded-xl border-2 border-transparent transition-colors")
    const info = el("div", "min-w-0")
    // whitespace-normal: SurveyJS sets white-space: nowrap on the question container, which
    // line-clamp inherits and turns into a one-line ellipsis (#4113).
    info.append(el("p", "text-sm font-semibold text-slate-800 leading-snug whitespace-normal line-clamp-3", name && name !== sku ? name : sku))
    const meta = el("p", "flex items-center gap-2 text-[11px] text-slate-400")
    meta.append(el("span", "font-mono truncate", sku))
    const echo = el("span", "hidden shrink-0 font-semibold text-amber-600")
    meta.append(echo)
    info.append(meta)
    // Card #4692 -- own line below the meta row, never inline with the I/O/C/X
    // buttons, so its presence/absence never shifts a button the tasker is
    // about to tap (icon-patterns.md "Toggle Buttons").
    const incomingBadge = el("span", "hidden items-center mt-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold")
    applyIncomingBadge(incomingBadge, incomingHint)
    info.append(incomingBadge)

    const group = el("div", "grid grid-cols-4 gap-2")
    group.setAttribute("role", "group")
    group.setAttribute("aria-label", `${name || sku} ranging status`)
    const buttons = values.map(value => {
      const button = el("button", "h-11 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-60", value)
      button.type = "button"
      button.dataset.value = value
      button.setAttribute("aria-label", RANGING_LABELS[value])
      button.addEventListener("click", () => tap(rowState, value))
      group.append(button)
      return button
    })

    row.append(info, group)
    list.append(row)

    const rowState = { sku, hint, incoming: incomingHint, row, echo, buttons, blob: null }
    rows.push(rowState)
  })

  function tap(rowState, value) {
    if (question.isReadOnly) return
    lastTappedIndex = rows.indexOf(rowState)
    const next = { ...(question.value || {}) }
    // Spread keeps any other keys on the row (e.g. stock_event_id) as they were.
    next[rowState.sku] = { ...(next[rowState.sku] || {}), status: value }
    question.value = next
    paint()
  }

  function paintRow(rowState) {
    const status = currentStatus(rowState.sku)
    rowState.row.dataset.confirmed = status ? "true" : "false"
    rowState.buttons.forEach(button => {
      const value = button.dataset.value
      const state = value === status ? "selected"
                  : (!status && value === rowState.hint) ? "ghost"
                  : "idle"
      applyRangingState(button, value, state)
      button.disabled = !!question.isReadOnly
      button.setAttribute("aria-pressed", String(value === status))
      button.title = state === "ghost" ? `Last visit: ${RANGING_LABELS[value]} – tap to confirm` : RANGING_LABELS[value]
    })
    const differs = status && rowState.hint && status !== rowState.hint
    rowState.echo.classList.toggle("hidden", !differs)
    rowState.echo.textContent = differs ? `was ${rowState.hint}` : ""
  }

  function paintCounter() {
    const total = rows.length
    const done = rows.filter(r => r.row.dataset.confirmed === "true").length
    const complete = done === total
    counter.textContent = complete ? `All ${total} confirmed` : `${done} of ${total} confirmed`
    counter.classList.toggle("text-green-700", complete)
    counter.classList.toggle("text-slate-700", !complete)
    pillCounter.textContent = complete ? `✓ ${total}/${total}` : `${done}/${total}`
    pillCounter.classList.toggle("text-green-700", complete)
    pillCounter.classList.toggle("text-slate-700", !complete)
    nextButton.classList.toggle("hidden", complete)
    pill.classList.toggle("pr-3", complete)
    pill.classList.toggle("pr-0.5", !complete)
  }

  function paint() {
    rows.forEach(paintRow)
    paintCounter()
    paintPinned()
  }

  // Search (#3070): rows are filtered by the same survey variables the search
  // bar sets, not by SurveyJS row visibility, so it never touches answers.
  function applySearch() {
    const term = String(survey.getVariable(`${searchVarName}_lower`) || "")
    rows.forEach(rowState => {
      rowState.blob ??= String(survey.getVariable(`${searchVarName}_row_${rowState.sku}`) || "")
      rowState.row.classList.toggle("hidden", !!term && !rowState.blob.includes(term))
    })
    noMatches.classList.toggle("hidden", rows.some(r => !r.row.classList.contains("hidden")))
  }

  function jumpToNextUnconfirmed() {
    const open = rows.filter(r => r.row.dataset.confirmed !== "true" && !r.row.classList.contains("hidden"))
    if (!open.length) return
    const target = open.find(r => rows.indexOf(r) > lastTappedIndex) || open[0]
    lastTappedIndex = rows.indexOf(target) - 1
    highlight(target)
  }

  // Scrolls a row to the centre and flashes its border. Every row carries a
  // transparent border of the same width, so only the colour changes.
  function highlight(target) {
    // Manual scroll, not scrollIntoView: on iOS a smooth scrollIntoView can move the
    // visual viewport and overlap the keyboard closing (#4114).
    const box = target.row.getBoundingClientRect()
    const top = box.top + window.scrollY - (window.innerHeight - box.height) / 2
    window.scrollTo({ top: Math.max(0, top), behavior: "instant" })
    target.row.classList.replace("border-transparent", "border-sapphire/40")
    setTimeout(() => target.row.classList.replace("border-sapphire/40", "border-transparent"), 1500)
  }


  // Repaint when the value changes from elsewhere (draft restore, data load).
  question.__rangingConfirmHandler = (_sender, options) => {
    if (options.name === question.name) paint()
  }
  survey.onValueChanged.add(question.__rangingConfirmHandler)

  htmlElement.addEventListener("matrix-search-changed", applySearch)

  // Hide the native table, keep the search bar and any validation errors.
  const content = htmlElement.querySelector(".sd-element__content") || htmlElement
  content.querySelectorAll("table").forEach(table => {
    // Important modifier: SurveyJS's own sd-table-wrapper display rule otherwise wins.
    (table.closest(".sd-table-wrapper") || table).classList.add("!hidden")
  })
  content.append(container)

  // Scan button inside the search box, beside the clear x.
  const searchWrapper = htmlElement.querySelector(".matrix-dropdown-search-wrapper")
  const barcodeToSku = new Map(Object.entries(barcodes || {}).map(([sku, code]) => [stripZeros(code), sku]))
  if (searchWrapper && scannerAvailable() && barcodeToSku.size) {
    const scanButton = el("button", "absolute right-0 top-1/2 -translate-y-1/2 size-11 flex items-center justify-center text-slate-500 active:text-slate-800")
    scanButton.type = "button"
    scanButton.title = "Scan barcode"
    scanButton.setAttribute("aria-label", "Scan barcode")
    scanButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"/></svg>'
    scanButton.addEventListener("click", () => {
      activeScanHandler = handleScan
      activeScanQuestionName = question.name
      document.dispatchEvent(new CustomEvent("ranging-barcode:open"))
    })
    searchWrapper.append(scanButton)
    // Room for the scan button: widen the input's right padding, shift the clear x left.
    const searchInput = searchWrapper.querySelector("input")
    searchInput?.classList.replace("pr-8", "pr-20")
    searchWrapper.querySelector("button.absolute.right-2")?.classList.replace("right-2", "right-11")
  }

  // Pinned product card (#4120). `pinned` holds the card for the most recent
  // matching scan; paint() keeps its buttons and "was X" echo in step with the row.
  let pinned = null
  let noMatchTimer = null

  function clearPinned() {
    pinned?.node.remove()
    pinned = null
  }

  function pinRow(rowState) {
    const template = document.getElementById(PINNED_TEMPLATE_ID)
    const slot = document.querySelector(PINNED_SLOT_SELECTOR)
    if (!template || !slot) return false

    const node = template.content.firstElementChild.cloneNode(true)
    const part = key => node.querySelector(`[data-pinned="${key}"]`)
    part("name").textContent = rowState.row.querySelector("p")?.textContent || rowState.sku
    part("sku").textContent = rowState.sku
    part("group").setAttribute("aria-label", `${part("name").textContent} ranging status`)
    // Card #4692 -- same badge as the row, absent on a pinned-card template
    // built before this card (older cached HTML offline).
    if (part("incoming")) applyIncomingBadge(part("incoming"), rowState.incoming)

    const buttons = [...node.querySelectorAll("button[data-value]")]
    buttons.forEach(button => {
      if (!values.includes(button.dataset.value)) return button.remove()
      button.addEventListener("click", () => tap(rowState, button.dataset.value))
    })

    slot.replaceChildren(node)
    pinned = { rowState, node, echo: part("echo"), buttons: buttons.filter(b => b.isConnected) }
    paintPinned()
    return true
  }

  function paintPinned() {
    if (!pinned) return
    if (!pinned.node.isConnected) { pinned = null; return }
    const { rowState, buttons, echo } = pinned
    const status = currentStatus(rowState.sku)
    buttons.forEach(button => {
      const value = button.dataset.value
      const state = value === status ? "selected"
                  : (!status && value === rowState.hint) ? "ghost"
                  : "idle"
      applyRangingState(button, value, state)
      button.disabled = !!question.isReadOnly
      button.setAttribute("aria-pressed", String(value === status))
      button.title = state === "ghost" ? `Last visit: ${RANGING_LABELS[value]} – tap to confirm` : RANGING_LABELS[value]
    })
    const differs = status && rowState.hint && status !== rowState.hint
    echo.classList.toggle("hidden", !differs)
    echo.textContent = differs ? `was ${rowState.hint}` : ""
  }

  function showNoMatch() {
    const toast = document.getElementById(NO_MATCH_ID)
    toast?.classList.remove("hidden")
    clearTimeout(noMatchTimer)
    noMatchTimer = setTimeout(() => toast?.classList.add("hidden"), NO_MATCH_VISIBLE_MS)
  }

  function handleScan(barcode) {
    const sku = barcodeToSku.get(stripZeros(barcode))
    const target = sku && rows.find(r => r.sku === sku)
    if (!target) {
      // Drop the previous pin so a stale product is never left under the camera to be tapped by mistake.
      clearPinned()
      showNoMatch()
      return
    }

    document.getElementById(NO_MATCH_ID)?.classList.add("hidden")
    if (pinRow(target)) return

    // No pinned card on this page: clear a search that is hiding the row and
    // scroll to it. The tasker still taps a status.
    if (target.row.classList.contains("hidden")) {
      const searchInput = searchWrapper?.querySelector("input")
      if (searchInput) {
        searchInput.value = ""
        searchInput.dispatchEvent(new Event("input"))
      }
    }
    highlight(target)
  }

  applySearch()
  paint()
  // A re-render of the question that opened the (still open) scanner takes over its scans.
  if (activeScanHandler && activeScanQuestionName === question.name) activeScanHandler = handleScan
  question.__rangingCleanups.push(watchScanDockSpacer(container, () => activeScanHandler === handleScan))
  question.__rangingCleanups.push(clearPinned) // the old render's pinned card would answer for stale rows
  question.__rangingCleanups.push(watchFloatingChrome({ pill, searchWrapper, container }))
}

// The scanner strip (#4120) is fixed at the top, so while it is open the list
// gets an in-flow spacer the height of the strip plus any pinned card. Without
// it the first rows would sit under the camera with no way to scroll them clear.
// Only the question that opened the scanner reserves the space.
function watchScanDockSpacer(container, isActive) {
  const spacer = el("div", "hidden")
  container.prepend(spacer)
  let observer = null

  const sync = dock => { spacer.style.height = `${dock.offsetHeight}px` }
  const onOpened = () => {
    const dock = document.getElementById(SCANNER_ID)
    if (!dock || !isActive()) return
    spacer.classList.remove("hidden")
    sync(dock)
    if (typeof ResizeObserver === "undefined") return
    observer?.disconnect()
    observer = new ResizeObserver(() => sync(dock))
    observer.observe(dock)
  }
  const onClosed = () => {
    observer?.disconnect()
    observer = null
    spacer.classList.add("hidden")
  }
  document.addEventListener("ranging-barcode:opened", onOpened)
  document.addEventListener("ranging-barcode:closed", onClosed)
  if (!document.getElementById(SCANNER_ID)?.classList.contains("hidden")) onOpened() // re-render while open

  return () => {
    document.removeEventListener("ranging-barcode:opened", onOpened)
    document.removeEventListener("ranging-barcode:closed", onClosed)
    observer?.disconnect()
    spacer.remove()
  }
}

// Floating chrome: the progress pill and the docked search box. Both are
// `fixed`, so they need to know where the visible page area starts.
//
// Field app: the page itself scrolls under a 56px sticky header, so the Tailwind
// classes below are the whole story. Main WIN page (/responses/:id): the survey
// scrolls inside a pane under the site nav, so the area is that pane's box;
// positions are then measured and set at runtime (the only inline style here,
// because the pane's position isn't known to CSS). Detected, not configured: a
// scrolling ancestor of the question means "pane".
//
// The search box is docked by switching the SAME element to fixed (focus, typed
// text, scan and clear stay as they are); an invisible copy holds its place in
// the flow, so nothing jumps either way. The pill sits to its right.
//
// #4114: the box is never docked or undocked while it holds focus. Switching a
// focused input between in-flow and fixed with the iOS keyboard up can leave the
// layout viewport shrunk afterwards, stranding the fixed bottom nav mid-page. The
// change waits for focusout, and the viewport is nudged once the keyboard closes.
const FOCUS_SETTLE_MS = 150
const FIELD_HEADER_PX = 56
const FLOW_CLASSES = ["relative", "w-full", "mb-3"]
const DOCK_CLASSES = ["fixed", "top-16", "left-3", "right-32", "z-[9]", "bg-white", "rounded-xl", "shadow-md"]
const PANE_GAP_PX = 8
const PILL_ROOM_PX = 128 // keeps the docked search clear of the pill

// The nearest ancestor that really scrolls (content taller than its box), or null.
function scrollingPane(from) {
  for (let node = from.parentElement; node && node !== document.body; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight + 1) return node
  }
  return null
}

// The visible area the floating chrome lives in, in viewport coordinates.
function visibleArea(container) {
  const pane = scrollingPane(container)
  if (!pane) return { pane: null, top: FIELD_HEADER_PX, bottom: window.innerHeight, left: 0, right: window.innerWidth }

  const box = pane.getBoundingClientRect()
  return { pane, top: box.top, bottom: box.bottom, left: box.left, right: box.left + pane.clientWidth }
}

function watchFloatingChrome({ pill, searchWrapper, container }) {
  // A 1px marker just before the search box: once it passes above the visible
  // area, the real box has scrolled away.
  let marker = null
  if (searchWrapper) {
    marker = el("div", "h-px w-full")
    searchWrapper.before(marker)
  }
  let placeholder = null

  const clearPlacement = element => {
    element.style.removeProperty("top")
    element.style.removeProperty("left")
    element.style.removeProperty("right")
  }
  // Pane mode only: pin to the pane's top corners. Field mode uses the classes.
  const place = (element, area, { left, right }) => {
    if (!area.pane) return clearPlacement(element)
    element.style.top = `${area.top + PANE_GAP_PX}px`
    if (left != null) element.style.left = `${left}px`
    element.style.right = `${window.innerWidth - area.right + right}px`
  }

  const dock = area => {
    if (!placeholder) {
      // The SAME element goes fixed (focus, typed text, scan and clear stay as
      // they are); an invisible copy holds its place, so nothing jumps.
      placeholder = searchWrapper.cloneNode(true)
      placeholder.classList.add("invisible")
      placeholder.setAttribute("aria-hidden", "true")
      placeholder.setAttribute("inert", "")
      searchWrapper.before(placeholder)
      searchWrapper.classList.remove(...FLOW_CLASSES)
      searchWrapper.classList.add(...DOCK_CLASSES)
    }
    place(searchWrapper, area, { left: area.left + 12, right: PILL_ROOM_PX })
  }
  const undock = () => {
    if (!placeholder) return
    placeholder.remove()
    placeholder = null
    searchWrapper.classList.remove(...DOCK_CLASSES)
    searchWrapper.classList.add(...FLOW_CLASSES)
    clearPlacement(searchWrapper)
  }
  const hasFocus = () => !!searchWrapper?.contains(document.activeElement)

  // Measured on scroll rather than with IntersectionObserver: SurveyJS's own
  // scroll containers made the observer miss the box leaving the viewport.
  let queued = false
  const update = () => {
    queued = false
    const area = visibleArea(container)
    const box = container.getBoundingClientRect()
    const questionVisible = box.bottom > area.top && box.top < area.bottom

    // Pill: only while some of the question is on screen, so with several
    // ranging-status questions only the one in view has a control.
    pill.classList.toggle("hidden", !questionVisible)
    pill.classList.toggle("flex", questionVisible)
    if (questionVisible) place(pill, area, { right: 12 })

    if (!marker || hasFocus()) return // dock changes apply on focusout instead
    marker.getBoundingClientRect().top < area.top && questionVisible ? dock(area) : undock()
  }
  const onScroll = () => {
    if (queued) return
    queued = true
    requestAnimationFrame(update)
  }
  // Keyboard-close recovery: iOS can keep a shrunk layout viewport after the
  // keyboard closes. Re-setting the same scroll position makes it recalculate.
  // Only fires if the viewport actually changed while the box had focus.
  const viewport = window.visualViewport
  let baseline = null
  let viewportChanged = false
  let settleTimer = null
  const viewportSize = () => `${window.innerHeight}x${Math.round(viewport?.height ?? 0)}`
  const scheduleSettle = () => {
    clearTimeout(settleTimer)
    settleTimer = setTimeout(() => {
      if (hasFocus()) return
      update()
      if (!viewportChanged) return
      viewportChanged = false
      window.scrollTo(window.scrollX, window.scrollY)
    }, FOCUS_SETTLE_MS)
  }
  const onFocusIn = () => { baseline = viewportSize() }
  const onViewportResize = () => {
    if (baseline && viewportSize() !== baseline) viewportChanged = true
    if (!hasFocus()) scheduleSettle()
  }
  searchWrapper?.addEventListener("focusin", onFocusIn)
  searchWrapper?.addEventListener("focusout", scheduleSettle)
  viewport?.addEventListener("resize", onViewportResize)

  window.addEventListener("scroll", onScroll, { passive: true, capture: true })
  window.addEventListener("resize", onScroll, { passive: true })
  update()

  return () => {
    clearTimeout(settleTimer)
    searchWrapper?.removeEventListener("focusin", onFocusIn)
    searchWrapper?.removeEventListener("focusout", scheduleSettle)
    viewport?.removeEventListener("resize", onViewportResize)
    window.removeEventListener("scroll", onScroll, { capture: true })
    window.removeEventListener("resize", onScroll)
    undock()
    marker?.remove()
  }
}
